import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Fade } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import NewCustomButton from './common/NewCustomButton';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';

const PwaInstallPrompt = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { showNotification } = useNotification();
  const { isDarkMode } = useTheme();

  // Detect if device is mobile and if app is already installed
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      return mobileRegex.test(userAgent.toLowerCase());
    };

    const checkInstalled = () => {
      // Check if app is already installed (PWA)
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }

      // Check for iOS installed web app
      if (window.navigator.standalone === true) {
        return true;
      }

      // Check localStorage for previous installation
      const installedTime = localStorage.getItem('plutoo_app_installed');
      if (installedTime) {
        const installedDate = new Date(parseInt(installedTime));
        const daysSinceInstall = (new Date() - installedDate) / (1000 * 60 * 60 * 24);

        // If installed more than 30 days ago, show prompt again
        if (daysSinceInstall < 30) {
          return true;
        }
      }

      return false;
    };

    setIsMobile(checkMobile());
    setIsInstalled(checkInstalled());
  }, []);

  // Intercept the browser's install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Update UI to notify the user they can add to home screen
      setIsInstallable(true);
      
      // Show the prompt after a delay if on mobile
      if (isMobile) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobile]);

  // Check if app was successfully installed
  useEffect(() => {
    const handleAppInstalled = (e) => {
      // Hide the prompt
      setShowPrompt(false);
      setIsInstallable(false);
      setIsInstalled(true);
      setShowSuccess(true);
      // Clear the deferredPrompt
      setDeferredPrompt(null);

      // Store installation timestamp
      localStorage.setItem('plutoo_app_installed', Date.now().toString());

      // Show success notification
      showNotification('🎉 Plutoo has been installed successfully!', 'success');

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [showNotification]);

  const handleInstallClick = () => {
    // Hide the prompt
    setShowPrompt(false);
    
    // Show the install prompt
    if (deferredPrompt) {
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          showNotification('Thanks for installing Plutoo!', 'success');
        } else {
          showNotification('You can install Plutoo later from the menu', 'info');
        }
        // We no longer need the prompt. Clear it for future use.
        setDeferredPrompt(null);
      });
    }
  };

  const handleClosePrompt = () => {
    setShowPrompt(false);
    // Don't show again for this session
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  // Don't show anything if not mobile, not installable, or already installed
  if (!isMobile || !isInstallable || !showPrompt || isInstalled) {
    return null;
  }

  // Check if the prompt was dismissed recently (within the last 24 hours)
  const dismissedTime = localStorage.getItem('pwaPromptDismissed');
  if (dismissedTime && Date.now() - parseInt(dismissedTime) < 86400000) {
    return null;
  }

  // Show success message if app was just installed
  if (showSuccess && isInstalled) {
    return (
      <Fade in={showSuccess} timeout={500}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            width: '90%',
            maxWidth: 400,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              borderRadius: '16px',
              bgcolor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <img
                src="/icons/icon-192x192.png"
                alt="Plutoo Logo"
                style={{
                  width: 48,
                  height: 48,
                  marginRight: 12,
                  borderRadius: '8px'
                }}
              />
              <Typography variant="h6" component="div" sx={{ color: '#10B981' }}>
                🎉 Installed Successfully!
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ textAlign: 'center', color: isDarkMode ? '#fff' : 'inherit' }}>
              Plutoo is now available on your home screen for quick access!
            </Typography>
          </Paper>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in={showPrompt} timeout={500}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: '90%',
          maxWidth: 400,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            borderRadius: '16px',
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            boxShadow: isDarkMode
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClosePrompt}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: isDarkMode ? '#fff' : 'inherit',
              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <img
              src="/icons/icon-192x192.png"
              alt="Plutoo Logo"
              style={{
                width: 56,
                height: 56,
                marginRight: 16,
                borderRadius: '12px'
              }}
            />
            <Box>
              <Typography variant="h6" component="div" sx={{ mb: 0.5 }}>
                Install Plutoo
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                Property Management App
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)' }}>
            Get quick access to your properties, tenants, and maintenance requests right from your home screen!
          </Typography>

          <NewCustomButton
            onClick={handleInstallClick}
            fullWidth
            variant="contained"
            size="large"
          >
            Add to Home Screen
          </NewCustomButton>

          <Typography
            variant="caption"
            sx={{
              mt: 2,
              color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
              textAlign: 'center'
            }}
          >
            Tap to install • No download required
          </Typography>
        </Paper>
      </Box>
    </Fade>
  );
};

export default PwaInstallPrompt; 